import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Clipboard, ClipboardCheck, CornerDownRight, UserPlus, Users } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { VALID_RPC_FUNCTIONS } from '@/lib/supabaseRpc';

interface WaitlistEntry {
  id: string;
  student_id: string;
  student_name: string;
  user_id: string;
  parent_name: string;
  parent_email: string;
  status: 'waiting' | 'notified' | 'enrolled' | 'expired';
  position: number;
  notes: string;
  created_at: string;
}

interface WaitlistManagerProps {
  classId: string;
  organizationId: string;
  className: string;
  hasAvailableSpots: boolean;
  maxCapacity: number;
  currentEnrollment: number;
}

const WaitlistManager: React.FC<WaitlistManagerProps> = ({
  classId,
  organizationId,
  className,
  hasAvailableSpots,
  maxCapacity,
  currentEnrollment,
}) => {
  const [waitlist, setWaitlist] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [waitlistId, setWaitlistId] = useState<string | null>(null);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<WaitlistEntry | null>(null);

  useEffect(() => {
    fetchWaitlist();
  }, [classId]);

  const fetchWaitlist = async () => {
    setLoading(true);
    try {
      // First, get or create the waitlist
      const { data: waitlistData, error: waitlistError } = await supabase
        .from('waitlists')
        .select('id')
        .eq('class_id', classId)
        .single();

      if (waitlistError && waitlistError.code !== 'PGRST116') {
        throw waitlistError;
      }

      let wlId = waitlistData?.id;

      // If waitlist doesn't exist yet, create it
      if (!wlId) {
        const { data: newWaitlist, error: createError } = await supabase
          .from('waitlists')
          .insert({
            class_id: classId,
            organization_id: organizationId,
          })
          .select('id')
          .single();

        if (createError) throw createError;
        wlId = newWaitlist.id;
      }

      setWaitlistId(wlId);

      // Validate RPC function name is in our list of known functions
      const rpcFunctionName = 'get_waitlist_entries_with_details';
      if (!VALID_RPC_FUNCTIONS.includes(rpcFunctionName)) {
        console.warn(`Warning: "${rpcFunctionName}" is not in the list of known RPC functions.`);
      }

      // Now get the waitlist entries with student details
      const { data: entries, error: entriesError } = await (supabase as any)
        .rpc('get_waitlist_entries_with_details', {
          waitlist_id_param: wlId
        });

      if (entriesError) throw entriesError;
      
      // Ensure entries are properly formatted before setting state
      const formattedEntries = Array.isArray(entries) ? entries.map(entry => ({
        ...entry,
        // Ensure all text fields are strings
        student_name: String(entry.student_name || ''),
        parent_name: String(entry.parent_name || ''),
        parent_email: String(entry.parent_email || ''),
        status: String(entry.status || 'waiting'),
        notes: String(entry.notes || ''),
        // Map queue_position to position for compatibility with existing code
        position: typeof entry.queue_position === 'number' ? entry.queue_position : Number(entry.queue_position || 0)
      })) : [];
      
      setWaitlist(formattedEntries as WaitlistEntry[]);
    } catch (error) {
      console.error('Error fetching waitlist:', error);
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  const handleNotifyStudent = async (entry: WaitlistEntry) => {
    setSelectedEntry(entry);
    setNotifyDialogOpen(true);
  };

  const confirmNotify = async () => {
    if (!selectedEntry || !waitlistId) return;
    
    try {
      // 1. Create a notification
      const { data: notification, error: notifError } = await supabase
        .from('notifications')
        .insert({
          content: `A spot has opened up in ${className}. You can now enroll from the waitlist.`,
          type: 'registration',
          created_by: (await supabase.auth.getUser()).data.user?.id,
          status: 'sent',
          title: 'Waitlist Spot Available',
          organization_id: organizationId,
        })
        .select()
        .single();
        
      if (notifError) throw notifError;
      
      // 2. Link notification to the parent user
      if (notification) {
        const { error: userNotifError } = await supabase
          .from('user_notifications')
          .insert({
            user_id: selectedEntry.user_id,
            notification_id: notification.id,
          });
          
        if (userNotifError) throw userNotifError;
      }
      
      // 3. Update waitlist entry status
      const { error: updateError } = await supabase
        .from('waitlist_entries')
        .update({ status: 'notified' })
        .eq('id', selectedEntry.id);
        
      if (updateError) throw updateError;
      
      toast.success('🔔 Parent Successfully Notified', {
        description: `${selectedEntry.student_name}'s parent has been notified about the available spot in ${className}. They can now enroll from the waitlist.`,
        duration: Infinity,
        action: {
          label: 'Manage Waitlist',
          onClick: () => {
            // The waitlist is already visible, just refresh it
            fetchWaitlist();
          }
        }
      });
      setNotifyDialogOpen(false);
      fetchWaitlist(); // Refresh the list
      
    } catch (error) {
      console.error('Error notifying parent:', error);
      toast.error('Failed to send notification');
    }
  };

  const handleEnrollStudent = async (entry: WaitlistEntry) => {
    if (!hasAvailableSpots) {
      toast.error('Cannot enroll student. Class is at maximum capacity.');
      return;
    }
    
    try {
      // 1. Create enrollment
      const { error: enrollError } = await supabase
        .from('enrollments')
        .insert({
          class_id: classId,
          student_id: entry.student_id,
          status: 'active',
          organization_id: organizationId,
        });
        
      if (enrollError) throw enrollError;
      
      // 2. Update waitlist entry
      const { error: updateError } = await supabase
        .from('waitlist_entries')
        .update({ status: 'enrolled' })
        .eq('id', entry.id);
        
      if (updateError) throw updateError;
      
      // 3. Create notification
      const { data: notification, error: notifError } = await supabase
        .from('notifications')
        .insert({
          content: `${entry.student_name} has been enrolled in ${className} from the waitlist.`,
          type: 'registration',
          created_by: (await supabase.auth.getUser()).data.user?.id,
          status: 'sent',
          title: 'Enrollment Confirmation',
          organization_id: organizationId,
        })
        .select()
        .single();
        
      if (notifError) throw notifError;
      
      // 4. Link notification to user
      if (notification) {
        const { error: userNotifError } = await supabase
          .from('user_notifications')
          .insert({
            user_id: entry.user_id,
            notification_id: notification.id,
          });
          
        if (userNotifError) throw userNotifError;
      }
      
      toast.success('✅ Student Successfully Enrolled from Waitlist', {
        description: `${entry.student_name} has been enrolled in ${className}. The parent will receive a confirmation notification.`,
        duration: Infinity,
        action: {
          label: 'View Enrollments',
          onClick: () => {
            // Refresh the waitlist to show updated status
            fetchWaitlist();
          }
        }
      });
      fetchWaitlist(); // Refresh the list
      
    } catch (error) {
      console.error('Error enrolling student:', error);
      toast.error('Failed to enroll student');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'notified':
        return <Badge variant="outline" className="bg-blue-100 text-blue-800">🔔 Notified</Badge>;
      case 'enrolled':
        return <Badge variant="default" className="bg-green-100 text-green-800">✅ Enrolled</Badge>;
      case 'expired':
        return <Badge variant="destructive">⏰ Expired</Badge>;
      default:
        return <Badge variant="secondary">⏳ Waiting</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Waitlist
            </CardTitle>
            <CardDescription>
              {waitlist.filter(e => e.status === 'waiting' || e.status === 'notified').length} students waiting for a spot
              {waitlist.some(e => e.status === 'notified') && (
                <div className="text-xs text-blue-600 mt-1">
                  🤖 Auto-notifications active - students notified automatically when spots open
                </div>
              )}
            </CardDescription>
          </div>
          <div className="text-sm">
            {currentEnrollment}/{maxCapacity} spots filled
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-8 text-center">
            <div className="animate-pulse">Loading waitlist...</div>
          </div>
        ) : waitlist.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-10 px-6">
            <div className="rounded-full bg-muted/30 p-3 mb-4">
              <Clipboard className="h-6 w-6 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium mb-1">No waitlist entries</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              When a class reaches capacity, students can join the waitlist to be notified when spots become available.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Position</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {waitlist
                .sort((a, b) => a.position - b.position)
                .map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">{entry.position !== undefined ? entry.position : '-'}</TableCell>
                    <TableCell className="font-medium">{entry.student_name || 'Unknown student'}</TableCell>
                    <TableCell>
                      {entry.parent_name || 'Unknown'} 
                      <span className="block text-xs text-muted-foreground">{entry.parent_email || 'No email'}</span>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(entry.status)}
                    </TableCell>
                    <TableCell>{entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {entry.status === 'waiting' && hasAvailableSpots && (
                          <Button 
                            size="sm" 
                            variant="default" 
                            onClick={() => handleEnrollStudent(entry)}
                          >
                            <UserPlus className="h-4 w-4 mr-1" />
                            Enroll
                          </Button>
                        )}
                        {entry.status === 'waiting' && !hasAvailableSpots && (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => handleNotifyStudent(entry)}
                          >
                            <CornerDownRight className="h-4 w-4 mr-1" />
                            Notify
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Notify Parent of Available Spot</DialogTitle>
            <DialogDescription>
              This will send a notification to the parent that a spot has opened up in the class.
              They will be able to enroll from the waitlist.
            </DialogDescription>
          </DialogHeader>
          
          {selectedEntry && (
            <div className="py-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium">Student:</span>
                  <span>{selectedEntry.student_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Parent:</span>
                  <span>{selectedEntry.parent_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Email:</span>
                  <span>{selectedEntry.parent_email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Notification:</span>
                  <span className="text-sm text-muted-foreground">
                    A spot has opened up in {className}. You can now enroll from the waitlist.
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifyDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmNotify}>
              <ClipboardCheck className="h-4 w-4 mr-1" />
              Confirm & Send Notification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default WaitlistManager; 